/**
 * What a subscription run's history row says about delivery. Each recipient is
 * checked on every send against the page: someone who may not see it — what it
 * shows is outside their subject areas, or restricted for them — is not sent
 * it, and the run names them. The rest is sent, failed, or suppressed by the
 * person's own email preference.
 */

export interface RunDeliveryCounts {
  email_sent_count: number;
  email_failed_count: number;
  email_suppressed_count: number;
  /** Not sent: this person may not see the page. A hub before access was enforced omits it. */
  email_skipped_access_count?: number;
  /** Who, by display name (email when there is none), once each. */
  skipped_access_names?: string[];
}

/**
 * "4 sent", "4 sent · 1 suppressed", "3 sent · 1 not sent". Zero parts are
 * dropped, except `sent`, which is always shown: a run that emailed nobody has
 * to say so.
 */
export function deliverySummaryText(run: RunDeliveryCounts): string {
  const parts = [`${run.email_sent_count} sent`];
  if (run.email_failed_count > 0) parts.push(`${run.email_failed_count} failed`);
  if (run.email_suppressed_count > 0) parts.push(`${run.email_suppressed_count} suppressed`);
  const skipped = run.email_skipped_access_count ?? 0;
  if (skipped > 0) parts.push(`${skipped} not sent`);
  return parts.join(" · ");
}

/** The state of a recipient the page was held back from. */
export const NOT_SENT_NOT_AVAILABLE = "Not sent — this page is not available to them.";

/** "Viewer One, Viewer Two: Not sent — this page is not available to them.", or null when nobody was held back. */
export function notSentNote(run: RunDeliveryCounts): string | null {
  const names = run.skipped_access_names ?? [];
  return names.length > 0 ? `${names.join(", ")}: ${NOT_SENT_NOT_AVAILABLE}` : null;
}
